import { useCallback, useState } from 'react';
import { Button, Dialog, InlineError, Input } from '../../components/ui';
import { useCreateSavedView, useDeleteSavedView, useSavedViews, useUpdateSavedView } from './hooks';
import { savedViewErrorMessage } from './queries';
import { SAVED_VIEW_NAME_MAX_LENGTH } from './types';
import type { SavedView, SavedViewFilters, SavedViewMode, SavedViewSort } from './types';

/**
 * Saved views (handoff 26): the toolbar's current arrangement, kept under a
 * name. Personal by design — the database shows each person exactly their
 * own rows, so there is no sharing to represent here.
 *
 * A view stores filters, sort and — since Phase 5 — the List or Board
 * selection, never task records. Applying one hands the whole arrangement
 * back; the chips row shows which one is active and whether the toolbar (or
 * the view toggle) has drifted from it since.
 */

function sameArrangement(
  view: SavedView,
  filters: SavedViewFilters,
  sort: SavedViewSort,
  viewMode: SavedViewMode,
): boolean {
  return (
    view.view_mode === viewMode &&
    JSON.stringify(view.filters) === JSON.stringify(filters) &&
    JSON.stringify(view.sort) === JSON.stringify(sort)
  );
}

export function SavedViewsBar({
  projectId,
  filters,
  sort,
  viewMode,
  onApply,
}: {
  projectId: string;
  filters: SavedViewFilters;
  sort: SavedViewSort;
  viewMode: SavedViewMode;
  /**
   * Hands a stored arrangement to the toolbar. viewMode null means "keep the
   * current one" (the All-tasks reset clears filters, not the view choice).
   */
  onApply: (filters: SavedViewFilters, sort: SavedViewSort, viewMode: SavedViewMode | null) => void;
}) {
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState('');

  const savedViews = useSavedViews();
  const createView = useCreateSavedView();
  const updateView = useUpdateSavedView();
  const deleteView = useDeleteSavedView();

  const closeSave = useCallback(() => setSaving(false), []);

  const views = (savedViews.data ?? []).filter((view) => view.project_id === projectId);
  const activeView = views.find((view) => view.id === activeViewId);
  const drifted = activeView !== undefined && !sameArrangement(activeView, filters, sort, viewMode);

  function report(cause: unknown) {
    setError(savedViewErrorMessage(cause instanceof Error ? cause.message : ''));
  }

  function apply(view: SavedView) {
    setError('');
    setConfirmingDelete(false);
    setActiveViewId(view.id);
    onApply(view.filters, view.sort, view.view_mode);
  }

  function reset() {
    setError('');
    setConfirmingDelete(false);
    setActiveViewId(null);
    onApply({}, {}, null);
  }

  function openSave() {
    setName(activeView?.name ?? '');
    setError('');
    setSaving(true);
  }

  function submitSave() {
    const trimmed = name.trim();
    if (trimmed === '') {
      setError('Give the view a name.');
      return;
    }
    setError('');
    createView.mutate(
      {
        projectId,
        name: trimmed,
        viewMode,
        filters,
        sort,
        groupBy: null,
        visibleColumns: null,
      },
      {
        onSuccess: (view) => {
          setActiveViewId(view.id);
          setSaving(false);
        },
        onError: report,
      },
    );
  }

  /** Re-save the active view's arrangement under its existing name. */
  function updateActive() {
    if (!activeView) {
      return;
    }
    setError('');
    updateView.mutate(
      {
        viewId: activeView.id,
        input: {
          name: activeView.name,
          viewMode,
          filters,
          sort,
          groupBy: activeView.group_by,
          visibleColumns: activeView.visible_columns,
        },
      },
      { onError: report },
    );
  }

  function removeActive() {
    if (!activeView) {
      return;
    }
    setError('');
    setConfirmingDelete(false);
    deleteView.mutate(activeView.id, {
      onSuccess: () => setActiveViewId(null),
      onError: report,
    });
  }

  // No views and nothing arranged: nothing to show or save yet, and the bar
  // would only be noise (the toolbar is the way to start). Board mode counts
  // as an arrangement — "my Board" with no filters is a real saved view.
  const anythingSet =
    Object.keys(filters).length > 0 || Object.keys(sort).length > 0 || viewMode === 'board';
  if (views.length === 0 && !anythingSet) {
    return null;
  }

  return (
    <div className="saved-views-bar">
      {error === '' ? null : <InlineError>{error}</InlineError>}

      <div className="saved-views-row" role="group" aria-label="Saved views">
        <button
          type="button"
          className={
            activeViewId === null ? 'saved-view-chip saved-view-chip--active' : 'saved-view-chip'
          }
          aria-pressed={activeViewId === null}
          onClick={reset}
        >
          All tasks
        </button>
        {views.map((view) => (
          <button
            key={view.id}
            type="button"
            className={
              view.id === activeViewId
                ? 'saved-view-chip saved-view-chip--active'
                : 'saved-view-chip'
            }
            aria-pressed={view.id === activeViewId}
            onClick={() => apply(view)}
          >
            {view.name}
            {view.id === activeViewId && drifted ? (
              <span aria-label="View has unsaved changes" className="saved-view-drift">
                •
              </span>
            ) : null}
          </button>
        ))}

        <span className="saved-views-actions">
          {anythingSet ? (
            <Button variant="ghost" size="sm" onClick={openSave}>
              Save view
            </Button>
          ) : null}
          {activeView && drifted ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={updateActive}
              disabled={updateView.isPending}
            >
              {updateView.isPending ? 'Updating…' : `Update “${activeView.name}”`}
            </Button>
          ) : null}
          {activeView ? (
            confirmingDelete ? (
              <>
                <Button variant="ghost" size="sm" onClick={() => setConfirmingDelete(false)}>
                  Keep
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={removeActive}
                  disabled={deleteView.isPending}
                >
                  Confirm delete
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Delete view ${activeView.name}`}
                onClick={() => setConfirmingDelete(true)}
              >
                Delete view
              </Button>
            )
          ) : null}
        </span>
      </div>

      <Dialog
        open={saving}
        title="Save view"
        onClose={closeSave}
        footer={
          <>
            <Button variant="ghost" onClick={closeSave}>
              Cancel
            </Button>
            <Button onClick={submitSave} disabled={createView.isPending}>
              {createView.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        }
      >
        <Input
          label="View name"
          value={name}
          maxLength={SAVED_VIEW_NAME_MAX_LENGTH}
          placeholder="My overdue tasks"
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              submitSave();
            }
          }}
        />
        <p className="task-panel-text">
          Saves the current view, search, filters and sort as a personal view. Only you see it.
        </p>
      </Dialog>
    </div>
  );
}
